import { Link } from 'react-router';
import type { Billing, Database, Project } from '@tallypg/contracts';
import { formatBytes, formatDate, formatMoney, useApi, type Forecast } from '../api';
import { StatusBadge } from '../components';

export function OverviewPage() {
  const billing = useApi<Billing>('/billing');
  const dbs = useApi<{ databases: Database[] }>('/databases');
  const projects = useApi<{ projects: Project[] }>('/projects');
  const forecast = useApi<Forecast>('/forecast');
  const b = billing.data;
  const f = forecast.data?.account;
  return (
    <div>
      <h1>Overview</h1>
      {b?.state === 'payment_required' && (
        <div className="banner danger">
          <strong>Payment required.</strong> Your databases are paused until you add credit. Your data is
          safe. <Link to="/billing">Add credit</Link>
        </div>
      )}
      <div className="grid cols-3">
        <div className="card">
          <div className="stat">Balance</div>
          <div className="stat-value">{b ? formatMoney(b.balanceMicroUsd) : '…'}</div>
          {b?.trialExpiresAt && (
            <div className="muted">
              includes trial credit until {new Date(b.trialExpiresAt).toLocaleDateString()}
            </div>
          )}
        </div>
        <div className="card">
          <div className="stat">Spent this month</div>
          <div className="stat-value">{b ? formatMoney(b.monthToDateMicroUsd) : '…'}</div>
          <div className="muted">
            {f && f.sampleDays > 0 && f.dailyRateMicro > 0
              ? `on track for ${formatMoney(f.projectedMonthMicro)} by month end · ${formatMoney(f.dailyRateMicro)}/day`
              : 'storage, activity and transfer'}
          </div>
          {f && f.daysOfCredit !== null && (
            <div className={f.daysOfCredit < 7 ? 'warn' : 'muted'} style={{ marginTop: 4 }}>
              {f.daysOfCredit < 7
                ? `Credit runs out in about ${f.daysOfCredit} day${f.daysOfCredit === 1 ? '' : 's'} at this pace.`
                : `Credit lasts about ${f.daysOfCredit} days at this pace (${formatDate(f.creditRunsOutAt)}).`}{' '}
              {f.shortfallMicro > 0 && <Link to="/billing">Add credit</Link>}
            </div>
          )}
        </div>
        <div className="card">
          <div className="stat">Databases</div>
          <div className="stat-value">
            {dbs.data ? dbs.data.databases.filter((d) => d.state !== 'deleted').length : '…'}
          </div>
          <div className="muted">
            {projects.data
              ? `${projects.data.projects.length} project${projects.data.projects.length === 1 ? '' : 's'}`
              : ''}
          </div>
        </div>
      </div>
      <div className="card">
        <div className="row between">
          <h2>Recent databases</h2>
          <Link to="/databases" className="btn">
            New database
          </Link>
        </div>
        {dbs.data && dbs.data.databases.length === 0 && (
          <p className="muted">
            No databases yet. Create a project, then a database, and you will get a connection string within
            seconds. Every database is real PostgreSQL: use psql, your ORM, anything.
          </p>
        )}
        {dbs.data && dbs.data.databases.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Size</th>
                <th>Last activity</th>
                <th>This month</th>
              </tr>
            </thead>
            <tbody>
              {dbs.data.databases.slice(0, 8).map((d) => (
                <tr key={d.id}>
                  <td>
                    <Link to={`/databases/${d.id}`}>{d.name}</Link>
                  </td>
                  <td>
                    <StatusBadge state={d.state} />
                  </td>
                  <td>{formatBytes(d.sizeBytes)}</td>
                  <td>{formatDate(d.lastActivityAt)}</td>
                  <td>{formatMoney(d.monthToDateMicroUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
